import React, { useCallback, useEffect } from 'react';
import { RouteComponentProps } from 'react-router';
import DefaultLocales from 'globals/locales/default';
import { observer } from 'mobx-react';
import FsButton from 'ui-elements/buttons/FsButton';
import Routes from 'routes';
import FsDialogTitle from 'ui-elements/modals/FsDialogTitle';
import { useReportContext } from 'contexts/report/ReportContext';
import { Box } from '@material-ui/core';
import { TransactionType } from 'globals/types';
import { isSuccessState, revertSlugify } from 'globals/utils';
import FsDefaultFallbackComponent from 'ui-elements/loaders/FsDefaultFallbackComponent';
import {
  CustomDialog,
  DialogActions,
  DialogContent,
} from '../ReportModals.style-elements';
import RecurringTransactionBarGraphContainer from './RecurringTransactionBarGraphContainer';
import TransactionListContainer from './TransactionList';
import {
  getDialogTitle,
  getProperRecurringTransactions,
} from './ReportRecurringTransactionsModal.utils';

const { close: closeLabel } = DefaultLocales;

const ReportRecurringTransactionsModal: React.FC<
  RouteComponentProps<{
    id?: string;
    type?: string;
  }>
> = ({ match, history }) => {
  const { report } = useReportContext();
  const modalId = match.params?.id && +match.params?.id;
  const slugType = match.params?.type;
  const type = slugType && revertSlugify(slugType);
  const recurringTransactions = getProperRecurringTransactions(
    type as TransactionType,
    report
  );
  const dialogTitle = getDialogTitle(type as TransactionType);
  const { rows, status } = recurringTransactions;
  const recurringTransaction = rows.filter(({ id }) => id === modalId);

  const redirect = useCallback((): void => {
    history.push({ pathname: Routes.REPORT });
  }, [history]);

  const handleClose = (): void => {
    redirect();
  };

  useEffect(() => {
    if (!modalId && modalId !== 0) redirect();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!(Object as any).values(TransactionType).includes(type)) redirect();
    if (isSuccessState(status) && !recurringTransaction.length) redirect();
  }, [modalId, redirect, status, recurringTransaction.length, type]);

  return (
    <CustomDialog
      disableEnforceFocus
      open
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      data-test="report-recurring-transactions-modal"
    >
      <FsDialogTitle
        id="report-recurring-transactions-modal"
        data-test="report-recurring-transactions-modal-title"
        onClose={handleClose}
      >
        {dialogTitle}
      </FsDialogTitle>

      <DialogContent dividers>
        {!isSuccessState(status) ? (
          <FsDefaultFallbackComponent
            status={status}
            withShadow={false}
            data-test="default-fallback-component"
          />
        ) : (
          <Box>
            {recurringTransaction.map(({ id, ...props }) => (
              <Box key={id}>
                <RecurringTransactionBarGraphContainer id={id} {...props} />
                <TransactionListContainer />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <FsButton onClick={handleClose} color="primary" variant="contained">
          {closeLabel}
        </FsButton>
      </DialogActions>
    </CustomDialog>
  );
};

export default observer(ReportRecurringTransactionsModal);
